import { BackendEnums } from 'api/codegen';

export const FEATURE_LABELS: Record<BackendEnums.FeaturesEnum, string> = {
  ticket_types: gettext('Ticket types'),
  ticket_create: gettext('Ticket creation'),
  refunds: gettext('Refunds'),
  documents: gettext('Documents'),
  credit_notes: gettext('Credit notes'),
  automation: gettext('Automation'),
  tags: gettext('Tags'),
  custom_fields: gettext('Custom fields'),
  views: gettext('Views'),
  processes: gettext('Processes'),
  canned_replies: gettext('Canned replies'),
  emailing: gettext('Emailing'),
  portal_edit: gettext('Portal customization'),
  marketplace: gettext('Marketplace'),
  api_access: gettext('API access'),
  webhooks: gettext('Webhooks'),
  user_signatures: gettext('User signatures'),
  shipping: gettext('Shipping management'),
  products_page: gettext('Products'),
  activities: gettext('Activities'),
  stats: gettext('Statistics'),
  cdp: gettext('Customers'),
  exchanges: gettext('Exchanges'),
  flow: gettext('Flow'),
  custom_roles: gettext('Custom roles'),
  mcp: gettext('MCP'),
  tracking_trackntrace: gettext('Track & Trace'),
  tracking_emailing: gettext('Tracking emails'),
  tracking_automation: gettext('Tracking automation'),
  tracking_analytics: gettext('Tracking analytics'),
  tracking_cost_analysis: gettext('Tracking cost analysis'),
};

// Feature name as a sentence object ("Try %s", "Unlock %s"), where inflecting languages need another case.
export const FEATURE_OBJECT_LABELS: Partial<Record<BackendEnums.FeaturesEnum, string>> = {
  tracking_cost_analysis: pgettext('feature name as a sentence object', 'Tracking cost analysis'),
};

export const FEATURE_DESCRIPTIONS: Record<BackendEnums.FeaturesEnum, string> = {
  ticket_types: gettext(
    'Create custom ticket types like exchanges, warranty service, or supplier complaints. Tailor your workflow to match your business needs.',
  ),
  ticket_create: gettext(
    'Create tickets manually from the dashboard. Handle cases that come in outside the customer portal.',
  ),
  refunds: gettext(
    'Process refunds directly from tickets. Configure refund methods, track refund status, and automate refund workflows.',
  ),
  documents: gettext(
    'Generate and attach documents like return labels, credit notes, or custom PDFs to your tickets automatically.',
  ),
  credit_notes: gettext(
    'Issue credit notes straight from a ticket. Retino creates them in your e-shop or accounting system and attaches the finished PDF.',
  ),
  automation: gettext(
    'Automate repetitive tasks: auto-assign, auto-respond, and trigger actions based on ticket state changes.',
  ),
  tags: gettext('Organize and categorize tickets with custom tags. Filter, search, and automate based on tags.'),
  custom_fields: gettext(
    'Add custom data fields to tickets and products. Capture the exact information your business needs.',
  ),
  views: gettext(
    'Create saved ticket views with custom filters and columns. Give each team member their own focused workspace.',
  ),
  processes: gettext(
    'Define multi-step workflows for each ticket type. Control how tickets move through states and who can do what at each step.',
  ),
  canned_replies: gettext(
    'Save and reuse message templates. Respond faster with pre-written replies for common situations.',
  ),
  emailing: gettext('Customize system emails with your branding. Control which notifications are sent and when.'),
  portal_edit: gettext(
    'Customize your customer portal appearance with colors, logo, texts, and layout that match your brand.',
  ),
  marketplace: gettext('Extend Retino with third-party integrations and plugins from the marketplace.'),
  api_access: gettext('Access the Retino API to build custom integrations and connect with your existing tools.'),
  webhooks: gettext(
    'Receive real-time notifications when events happen in Retino. Integrate with external systems automatically.',
  ),
  user_signatures: gettext('Set up personalized email signatures for each team member.'),
  shipping: gettext(
    'Manage shipping contracts, labels, and tracking. Automate shipping workflows for returns and exchanges.',
  ),
  products_page: gettext('View and manage your product catalog. Track return rates and product-level analytics.'),
  activities: gettext('Track all team activity in one place. See who did what and when across all tickets.'),
  stats: gettext('Access detailed analytics on tickets, return rates, ratings, and team performance.'),
  cdp: gettext('Unify customer data and history across orders and returns.'),
  exchanges: gettext(
    'Let customers choose another product instead of a refund. Retino creates the replacement order and keeps the whole exchange in one place.',
  ),
  flow: gettext('Automate work with AI-powered flows.'),
  custom_roles: gettext(
    'Build your own roles from individual rights. Give warehouse staff, accountants, or analysts exactly the access they need.',
  ),
  mcp: gettext('Connect AI assistants securely to your Retino workspace.'),
  tracking_trackntrace: gettext('Give customers a branded page with live shipment information.'),
  tracking_emailing: gettext('Send branded transactional emails about shipment progress.'),
  tracking_automation: gettext('Run automated actions when shipment events occur.'),
  tracking_analytics: gettext('Analyze delivery performance, exceptions, and customer ratings.'),
  tracking_cost_analysis: gettext('Compare carrier invoices with shipment revenue and margin.'),
};

export const FEATURE_ICONS: Record<BackendEnums.FeaturesEnum, UIIconName> = {
  ticket_types: 'collection',
  ticket_create: 'document-add',
  refunds: 'cash',
  documents: 'document-text',
  credit_notes: 'credit-card',
  automation: 'lightning-bolt',
  tags: 'tag',
  custom_fields: 'adjustments',
  views: 'view-grid',
  processes: 'clipboard-list',
  canned_replies: 'chat-alt',
  emailing: 'mail',
  portal_edit: 'color-swatch',
  marketplace: 'puzzle',
  api_access: 'code',
  webhooks: 'globe-alt',
  user_signatures: 'pencil',
  shipping: 'truck',
  products_page: 'shopping-bag',
  activities: 'clipboard-check',
  stats: 'chart-bar',
  cdp: 'database',
  exchanges: 'switch-horizontal',
  flow: 'sparkles',
  custom_roles: 'user-group',
  mcp: 'code',
  tracking_trackntrace: 'globe-alt',
  tracking_emailing: 'mail',
  tracking_automation: 'lightning-bolt',
  tracking_analytics: 'chart-bar',
  tracking_cost_analysis: 'cash',
};

// Mirrors backend PlanTierLevel (subscriptions/constants.py).
export const STANDARD_TIER_LEVEL = 10;
export const ADVANCED_TIER_LEVEL = 20;

// Features unlocked only by the Advanced tier (backend FEATURE_REQUIREMENTS is the source of truth).
const ADVANCED_FEATURES: string[] = ['cdp', 'exchanges', 'flow', 'custom_roles', 'tracking_cost_analysis'];

export function getFeatureTargetTierLevel(feature: string): number {
  return ADVANCED_FEATURES.includes(feature) ? ADVANCED_TIER_LEVEL : STANDARD_TIER_LEVEL;
}

// "Also included in Advanced" package list shown in upsell contexts.
export const ADVANCED_PACKAGE_HIGHLIGHTS = [
  gettext('Exchanges'),
  gettext('Customer profiles'),
  gettext('Retino Flow + 1,000 credits'),
  gettext('Advanced permissions'),
  gettext('AI processes'),
];

export const ADVANCED_COMING_SOON = gettext('More big releases are coming soon. Advanced gets them first.');

export const PRO_HIGHLIGHTS = [
  gettext('Automation & processes'),
  gettext('Custom ticket types & fields'),
  gettext('Documents & refunds'),
  gettext('API, webhooks & marketplace'),
  gettext('Team collaboration'),
];
